import {
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Card,
  CardContent,
  Grid2 as Grid,
  Divider,
  Avatar,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { formatDate, getAge } from '../utils/formatters';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cake as CakeIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  GridView as GridViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import personsService from '../services/personsService';
import { usePersons, useDuplicatePersons } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import PersonForm from '../components/forms/PersonForm';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import DuplicatesBanner from '../components/DuplicatesBanner';
import PersonMergeDialog from '../components/PersonMergeDialog';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useContextualHint } from '../hooks/useContextualHint';
import ContextualHint from '../components/ContextualHint';
import { filterPaper, pageHeader } from '../styles/pageStyles';

export default function PersonsPage() {
  const { getLabel, getIcon } = useThemeCustom();
  
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('persons_view_mode') || 'grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedPersonForAppointment, setSelectedPersonForAppointment] = useState(null);

  const handleViewModeChange = (_, newMode) => {
    if (!newMode) return;
    setViewMode(newMode);
    localStorage.setItem('persons_view_mode', newMode);
  };

  // ✅ Custom Hooks - React Query
  const { items: persons, loading, error, reload, create, update, deleteItem } = usePersons();
  const { duplicates, merge, isMerging } = useDuplicatePersons();
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const mergeDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const hint = useContextualHint('persons', {});

  // ✅ Handlers
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      setSearching(true);
      const response = await personsService.getAll({ search: searchQuery });
      setSearchResults(response);
    } catch (err) {
      showError('Erro ao buscar: ' + (err.response?.data?.message || err.message));
    } finally {
      setSearching(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleOpenAppointmentForm = (person) => {
    setSelectedPersonForAppointment(person);
  };

  const handleCloseAppointmentForm = () => {
    setSelectedPersonForAppointment(null);
  };

  const handleAppointmentCreated = () => {
    handleCloseAppointmentForm();
    showSuccess('Agendamento criado com sucesso!');
  };

  const handleCreate = async (data) => {
    try {
      await create(data);
      formDialog.handleClose();
      showSuccess('Pessoa criada com sucesso!');
    } catch (err) {
      showError('Erro ao criar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await update(formDialog.data.id, data);
      formDialog.handleClose();
      showSuccess('Pessoa atualizada com sucesso!');
    } catch (err) {
      showError('Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleMerge = async (mergeRequest) => {
    try {
      await merge(mergeRequest);
      mergeDialog.handleClose();
      showSuccess('Pessoas unificadas com sucesso!');
      reload();
    } catch (err) {
      showError('Erro ao unificar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando pessoas..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const displayPersons = searchResults !== null ? searchResults : (persons || []);

  return (
    <Box>
      <Box sx={pageHeader}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary.main">
            {getLabel('persons')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {getLabel('personsDescription')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => formDialog.handleOpen(null)}
        >
          Nova Pessoa
        </Button>
      </Box>

      <ContextualHint hint={hint} />

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Gerencie clientes, pacientes, alunos e demais pessoas vinculadas aos seus estabelecimentos.
          Apenas nome e telefone são obrigatórios.
        </Typography>
      </Alert>

      {/* Banner de Duplicatas */}
      <DuplicatesBanner
        duplicates={duplicates}
        onCardClick={(pair) => mergeDialog.handleOpen(pair)}
      />

      {/* Campo de Busca */}
      <Paper sx={filterPaper}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <Tooltip title="Grade">
              <ToggleButton value="grid"><GridViewIcon fontSize="small" /></ToggleButton>
            </Tooltip>
            <Tooltip title="Lista">
              <ToggleButton value="list"><ListViewIcon fontSize="small" /></ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          <TextField
            fullWidth
            placeholder="Buscar por nome, telefone, email ou CPF..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searching}
            sx={{ minWidth: 120 }}
          >
            {searching ? 'Buscando...' : 'Buscar'}
          </Button>
          {searchResults !== null && (
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              sx={{ minWidth: 120 }}
            >
              Limpar
            </Button>
          )}
        </Box>
        {searchResults !== null && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchResults.length} resultado(s) encontrado(s)
          </Typography>
        )}
      </Paper>

      {/* Empty State */}
      {displayPersons.length === 0 ? (
        <EmptyState
          icon={PersonIcon}
          title={searchResults !== null ? "Nenhum resultado encontrado" : "Nenhuma pessoa cadastrada"}
          message={searchResults !== null ? "Tente buscar com outros termos" : "Clique em 'Nova Pessoa' para começar"}
          action={
            searchResults === null ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => formDialog.handleOpen(null)}>
                Nova Pessoa
              </Button>
            ) : null
          }
        />
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {displayPersons.map((person) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={person.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
                <CardContent sx={{ flexGrow: 1, p: 1.5, '&:last-child': { pb: 1 } }}>
                  {/* Cabeçalho do Card */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                    <Avatar
                      src={person.photoUrl}
                      alt={person.name}
                      sx={{ width: 36, height: 36, bgcolor: 'grey.300', color: 'grey.600', flexShrink: 0 }}
                    >
                      {!person.photoUrl && <PersonIcon sx={{ fontSize: 22 }} />}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="bold" noWrap>{person.name}</Typography>
                      {person.gender && (
                        <Chip label={person.gender} size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem' }} />
                      )}
                    </Box>
                  </Box>

                  {/* Contato */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <PhoneIcon sx={{ fontSize: 14 }} color="action" />
                      <Typography variant="caption" color="text.secondary">{person.phone}</Typography>
                    </Box>
                    {person.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                        <EmailIcon sx={{ fontSize: 14 }} color="action" />
                        <Typography variant="caption" color="text.secondary" noWrap>{person.email}</Typography>
                      </Box>
                    )}
                    {person.birthDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CakeIcon sx={{ fontSize: 14 }} color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(person.birthDate)} ({getAge(person.birthDate)} anos)
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {person.cpf && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      <strong>CPF:</strong> {person.cpf}
                    </Typography>
                  )}

                  {(person.addressStreet || person.addressCity) && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <LocationIcon color="action" sx={{ mt: 0.25, fontSize: 14 }} />
                        <Box>
                          {person.addressStreet && (
                            <Typography variant="caption" color="text.secondary">
                              {person.addressStreet}{person.addressNumber && `, ${person.addressNumber}`}{person.addressComplement && ` - ${person.addressComplement}`}
                            </Typography>
                          )}
                          {(person.addressCity || person.addressState) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {person.addressCity}{person.addressState && ` - ${person.addressState}`}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}

                  {person.notes && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {person.notes}
                      </Typography>
                    </>
                  )}

                  {/* Ações */}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
                    <IconButton size="small" color="success" onClick={() => handleOpenAppointmentForm(person)} title="Agendar consulta" sx={{ p: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" color="primary" onClick={() => formDialog.handleOpen(person)} title="Editar" sx={{ p: 0.5 }}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => deleteDialog.handleOpen(person)} title="Excluir" sx={{ p: 0.5 }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: 40 }} />
                <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nascimento</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>CPF</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Endereço</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 110 }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayPersons.map((person) => (
                <TableRow key={person.id} hover>
                  <TableCell sx={{ py: 0.5 }}>
                    <Avatar src={person.photoUrl} alt={person.name} sx={{ width: 28, height: 28, bgcolor: 'grey.300', color: 'grey.600' }}>
                      {!person.photoUrl && <PersonIcon sx={{ fontSize: 16 }} />}
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>{person.name}</Typography>
                    {person.gender && (
                      <Chip label={person.gender} size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem' }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption">{person.phone}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption" noWrap>{person.email || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {person.birthDate
                      ? <Typography variant="caption">{formatDate(person.birthDate)} ({getAge(person.birthDate)} a)</Typography>
                      : <Typography variant="caption" color="text.disabled">—</Typography>
                    }
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption">{person.cpf || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {(person.addressStreet || person.addressCity)
                      ? <Typography variant="caption" noWrap>
                          {[person.addressStreet, person.addressNumber, person.addressCity, person.addressState].filter(Boolean).join(', ')}
                        </Typography>
                      : <Typography variant="caption" color="text.disabled">—</Typography>
                    }
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }} align="right">
                    <IconButton size="small" color="success" onClick={() => handleOpenAppointmentForm(person)} title="Agendar consulta" sx={{ p: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" color="primary" onClick={() => formDialog.handleOpen(person)} title="Editar" sx={{ p: 0.5 }}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => deleteDialog.handleOpen(person)} title="Excluir" sx={{ p: 0.5 }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Person Form Dialog */}
      <PersonForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/persons/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Appointment Form Dialog - auto-suficiente, carrega próprios dados */}
      {selectedPersonForAppointment && (
        <AppointmentFormDialog
          open={!!selectedPersonForAppointment}
          onClose={handleCloseAppointmentForm}
          onSuccess={handleAppointmentCreated}
          prefillData={{
            clientName: selectedPersonForAppointment.name,
            clientPhone: selectedPersonForAppointment.phone,
            clientEmail: selectedPersonForAppointment.email || '',
          }}
        />
      )}

      {/* Merge Dialog */}
      <PersonMergeDialog
        open={mergeDialog.open}
        onClose={mergeDialog.handleClose}
        pair={mergeDialog.data}
        onMerge={handleMerge}
        isMerging={isMerging}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
